import React, { useCallback, useEffect, useState } from 'react';
import {
  Dimensions,
  FlatList,
  Image as RNImage,
  Platform,
  TouchableHighlight,
  TouchableOpacity,
  View,
  ViewProps,
} from 'react-native';
import { Card, Icon, Text } from 'react-native-elements';
import lodash from 'lodash';
import SwiperFlatList from 'react-native-swiper-flatlist';

import {
  Advertisement,
  FullCategory,
  FullThumbtackCategory,
  Image as AdvertImage,
  Tag,
  ThumbtackHomecare,
  // ThumbtackCategoriesObject,
} from '../../../api/adserver';
import config from '../../config';
import ExploreImage from '../exploreimage';

import { trackAdvertView } from '../../lib/analytics';
import styles from './styles';
import { getTopImageUrl } from '../../lib/advert';

const debug = require('debug')('se:carousel');

const { width: windowWidth } = Dimensions.get('window');

const itemFeatureHeight = 240;
const widthFeaturePadding = 8;
const itemFeatureWidth = windowWidth - (60 + widthFeaturePadding);
const totalFeatureItemWidth = itemFeatureWidth + widthFeaturePadding;

const itemWidthPadding = 20;
const itemHeight = 205;
const itemWidth = 219;
const itemCardPadding = 20;

const totalItemWidth = itemWidth + itemWidthPadding;

const widthMiniPadding = 12;
const itemMiniHeight = 55;
const itemMiniWidth = 70;
const totalMiniItemWidth = itemMiniWidth + widthMiniPadding;

const featureItemHeight =
  itemFeatureWidth * config.theme.advert.imageHeightRatio; // 194;

export interface SlideData {
  id: string;
  imageUrl: string;
  title?: string;
  subtitle?: string;
}

export interface AdvertSlideData {
  advert: Advertisement;
}

type onPressHandler = (item: any) => void;
type onPressCategoryHandler = (
  item: FullCategory | FullThumbtackCategory,
) => void;

export interface SlideProps {
  data: SlideData;
  onPress?: onPressHandler;
  hideText?: boolean;
  margin?: number;
}

export interface PhotoSlideProps {
  data: AdvertImage;
  onPress?: onPressHandler;
  hideText?: boolean;
  margin?: number;
}

export interface AdvertSlideProps {
  data: AdvertSlideData;
  onPress?: onPressHandler;
  onPressCategory?: onPressCategoryHandler;
  hideText?: boolean;
  margin?: number;
  showViewMore?: boolean;
  onViewMore?: (item?: any) => void;
}

export interface MiniSlideData {
  id: string;
  icon: string;
  title?: string;
  local?: boolean;
}
export interface MiniSlideProps {
  data: MiniSlideData;
  onPress: onPressHandler;
  singleLine?: boolean;
}

export interface TagSlideProps {
  data: Tag;
  onPress: onPressHandler;
  isSelected: () => boolean;
}

export interface BadgeSlideProps {
  data: Tag;
  onPress: onPressHandler;
}

const Slide: React.FC<SlideProps> = ({ data, onPress }) => {
  return (
    <Card containerStyle={[styles.slideContainer]}>
      <TouchableHighlight
        onPress={() => {
          if (onPress) {
            onPress(data);
          }
        }}
      >
        <Card.Image source={{ uri: data.imageUrl }} resizeMode="cover" />
      </TouchableHighlight>
      <View style={styles.slideTextContainer}>
        {data.title && (
          <TouchableOpacity onPress={onPress}>
            <Text style={[styles.slideText, styles.slideTextLarge]}>
              {data.title}
            </Text>
          </TouchableOpacity>
        )}
        {data.subtitle && (
          <TouchableOpacity onPress={onPress}>
            <Text style={[styles.slideText, styles.slideTextMedium]}>
              {data.subtitle}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </Card>
  );
};

const AdvertSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onPress,
}) => {
  let imageUri = lodash.get(advert, 'hero_image.formats.medium.url', undefined);
  if (!imageUri) {
    imageUri = lodash.get(advert, 'hero_image.url', undefined);
  }
  if (!imageUri) {
    imageUri = getTopImageUrl(advert);
  }
  const handleOnPress = useCallback(() => {
    if (onPress) {
      onPress(advert);
    }
  }, [advert, onPress]);
  return (
    <Card containerStyle={[styles.slideContainer]}>
      <TouchableOpacity onPress={handleOnPress}>
        <ExploreImage uri={imageUri} style={styles.advertImage} />
      </TouchableOpacity>
      <View style={styles.slideTextContainer}>
        {advert.headline && (
          <TouchableOpacity onPress={handleOnPress}>
            <Text style={[styles.slideText, styles.slideTextLarge]}>
              {advert.headline}
            </Text>
          </TouchableOpacity>
        )}
        {advert.bi_line && (
          <TouchableOpacity onPress={handleOnPress}>
            <Text style={[styles.slideText, styles.slideTextMedium]}>
              {advert.bi_line}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </Card>
  );
};

const arrowSize = 26;
const arrowName = 'arrow-forward';

const ViewMoreFeatureSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onViewMore,
}) => {
  return (
    <TouchableOpacity
      onPress={() => {
        if (onViewMore) {
          onViewMore(advert);
        }
      }}
    >
      <View style={styles.featuredSlideContainer}>
        <View style={[styles.viewMoreSlide]}>
          <Icon
            tvParallaxProperties={null}
            name={arrowName}
            size={arrowSize}
            color={config.theme.advert.headingTextColor}
            style={styles.viewMoreArrow}
          />
          <Text>See More</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const ViewMoreAdvertSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onViewMore,
}) => {
  return (
    <Card containerStyle={[styles.slideContainer]}>
      <TouchableOpacity
        onPress={() => {
          if (onViewMore) {
            onViewMore(advert);
          }
        }}
      >
        <View style={styles.advertViewMoreSlideContainer}>
          <View style={[styles.viewMoreSlide]}>
            <Icon
              tvParallaxProperties={null}
              name={arrowName}
              size={arrowSize}
              color={config.theme.advert.headingTextColor}
              style={styles.viewMoreArrow}
              iconStyle={styles.iconBase}
            />
            <Text>Find More</Text>
          </View>
        </View>
      </TouchableOpacity>
    </Card>
  );
};

const FeatureSlide: React.FC<AdvertSlideProps> = ({
  data: { advert },
  onPress,
}) => {
  const topImageUrl = getTopImageUrl(advert);
  const imageUrl = advert.hero_image ? advert.hero_image.url : topImageUrl;
  return (
    <TouchableOpacity
      onPress={() => {
        if (onPress) {
          onPress(advert);
        }
      }}
    >
      <View style={styles.featuredSlideContainer}>
        <ExploreImage uri={imageUrl} style={styles.featuredImage} />

        {!advert.hide_overlay_text && (
          <View style={styles.slideFeatureTextContainer}>
            {advert.headline && (
              <Text
                style={[styles.slideFeatureText, styles.slideFeatureTextLarge]}
              >
                {advert.headline}
              </Text>
            )}
            {advert.bi_line && (
              <Text
                style={[styles.slideFeatureText, styles.slideFeatureTextMedium]}
              >
                {advert.bi_line}
              </Text>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const PhotoSlide: React.FC<PhotoSlideProps> = ({ data }) => {
  return (
    <View style={styles.featuredSlideContainer}>
      <ExploreImage
        uri={data.url}
        resizeMode="contain"
        style={styles.photoImage}
      />
    </View>
  );
};

const MiniSlide: React.FC<MiniSlideProps> = ({
  data,
  onPress,
  singleLine = false,
}) => {
  const containerStyles: any[] = [styles.slideMiniContainer];
  const textStyles: any[] = [styles.slideMiniText];
  const textContainerStyles: any[] = [styles.slideMiniTextContainer];
  if (singleLine) {
    containerStyles.push(styles.slideMiniContainerSingleLine);
    textStyles.push(styles.slideMiniTextSingleLine);
    textContainerStyles.push(styles.slideMiniTextContainerSingleLine);
  }
  const imageParams: any = {
    style: styles.slideMiniImage,
    // defaultSource={{uri: data.icon}}
    resizeMode: 'contain',
    resizeMethod: 'scale',
    // resizeMode: 'cover',
  };
  if (data.local && Platform.OS === 'ios') {
    imageParams.defaultSource = { uri: data.icon };
  } else {
    imageParams.source = { uri: data.icon };
  }
  return (
    <TouchableHighlight
      onPress={() => {
        if (onPress) {
          onPress(data);
        }
      }}
      activeOpacity={1.0}
      underlayColor="#cccccc"
    >
      <View style={containerStyles}>
        {data.icon && <RNImage {...imageParams} />}
        <View style={textContainerStyles}>
          {data.title && <Text style={textStyles}>{data.title}</Text>}
        </View>
      </View>
    </TouchableHighlight>
  );
};

const BadgeSlide: React.FC<BadgeSlideProps> = ({ data, onPress }) => {
  const style: any[] = [styles.slideBadgeContainer];
  const textStyle: any[] = [styles.slideTagsText];
  return (
    <>
      {data.name && (
        <TouchableHighlight
          onPress={() => {
            if (onPress) {
              onPress(data);
            }
          }}
          activeOpacity={1.0}
          style={style}
          underlayColor="#cccccc"
        >
          {data.name && <Text style={textStyle}>{data.name}</Text>}
        </TouchableHighlight>
      )}
    </>
  );
};

const TagSlide: React.FC<TagSlideProps> = ({ data, onPress, isSelected }) => {
  const style: any[] = [styles.slideTagsContainer];
  const textStyle: any[] = [styles.slideTagsText];
  if (isSelected()) {
    style.push(styles.slideTagsSelected);
    textStyle.push(styles.slideTagsSelectedText);
  }
  return (
    <TouchableHighlight
      onPress={() => {
        if (onPress) {
          onPress(data);
        }
      }}
      activeOpacity={1.0}
      style={style}
      underlayColor="#cccccc"
    >
      {data.name && <Text style={textStyle}>{data.name}</Text>}
    </TouchableHighlight>
  );
};

export enum DisplayType {
  adverts,
  badges,
  featured,
  full,
  mini,
  photo,
  tags,
}

export interface Props {
  placeholder?: string;
  displayType?: DisplayType;
  items?: Array<any>;
  selectedItems?: Array<any>;
  onPress?: (item: Advertisement) => void;
  onPressBadge?: (item: Tag) => void;
  onPressCategory?: (item: FullCategory | FullThumbtackCategory) => void;
  onPressData?: (item: any) => void;
  onSelectedListChange?: (items: any[]) => void;
  onViewMore?: () => void;
  showViewMore?: boolean;
  name?: string;
  loop?: boolean;
  handleViewableItemsChanged?: (items: any) => void;
  singleLine?: boolean;
  initialSelected?: any[];
  renderItem?: ((item: any) => JSX.Element | null) | null;
}

const CarouselFC: React.FC<Props> = (props) => {
  const {
    displayType,
    handleViewableItemsChanged,
    items,
    loop,
    name,
    onPress,
    onPressBadge,
    onPressCategory,
    onPressData,
    onSelectedListChange,
    onViewMore,
    showViewMore,
    singleLine,
    initialSelected,
    renderItem: customRenderItem,
  } = props;
  const [selectedItems, setSelectedItems] = useState<any>(
    initialSelected ? initialSelected : [],
  );
  useEffect(() => {
    setSelectedItems(initialSelected ? initialSelected : []);
  }, [initialSelected, setSelectedItems]);

  useEffect(() => {
    if (onSelectedListChange) {
      onSelectedListChange(selectedItems);
    }
  }, [onSelectedListChange, selectedItems]);

  const keyGen = useCallback(
    (e: Advertisement | ThumbtackHomecare, index: number) => {
      let key = `${name}-${e.id.toString()}-${index}`;
      return key;
    },
    [name],
  );

  const renderCell = ({
    index,
    style,
    ...viewProps
  }: ViewProps & { index: number }) => {
    const cellStyle = [style, { flex: 1 }];
    return <View style={cellStyle} {...viewProps} />;
  };

  const renderItem = useCallback(
    ({ item }: { item: any }) => {
      // console.log('renderItem (lib)');
      const onPressDataHandler = (itemData: any) => {
        debug(`on press data: ${itemData.id}`);
        if (onPressData) {
          onPressData(itemData);
        } else {
          debug('no onpressdata handler');
        }
      };
      const onPressHandler = (itemPress: Advertisement) => {
        console.log('onPressHandler', itemPress);
        debug(`on press: ${itemPress.id}`);
        if (onPress) {
          onPress(itemPress);
        } else {
          debug('no on press handler');
        }
      };

      const handleBadgeOnPress = (itemPress: Tag) => {
        debug(`on press: ${itemPress.id}`);
        if (onPressBadge) {
          onPressBadge(itemPress);
        } else {
          debug('no on press handler');
        }
      };

      const isSelected = (itemSelected: any) => {
        return !!lodash.find(
          selectedItems,
          (value) => value.id === itemSelected.id,
        );
      };

      const onToggleItem = (itemToggle: any) => {
        const found = isSelected(itemToggle);
        if (found) {
          // do nothing.  we want to leave selected
          // setSelectedItems([]);
        } else {
          setSelectedItems([itemToggle]);
        }
        // if (found) {
        //   setSelectedItems(
        //     lodash.filter(selectedItems, (value) => value.id !== itemToggle.id),
        //   );
        // } else {
        //   setSelectedItems([...selectedItems, itemToggle]);
        // }
      };

      if (item.showViewMore) {
        //  debug(`${name} - item.showViewMore is true`);
        switch (displayType) {
          case DisplayType.featured: {
            return (
              <ViewMoreFeatureSlide
                data={{ advert: item }}
                onPress={onPressHandler}
                onPressCategory={onPressCategory}
                margin={0}
                onViewMore={onViewMore}
                showViewMore={showViewMore}
              />
            );
          }
          case DisplayType.adverts: {
            return (
              <ViewMoreAdvertSlide
                data={{ advert: item }}
                onPress={onPressHandler}
                onPressCategory={onPressCategory}
                margin={0}
                onViewMore={onViewMore}
                showViewMore={showViewMore}
              />
            );
          }
        }
      }

      switch (displayType) {
        case DisplayType.mini: {
          return (
            <MiniSlide
              singleLine={singleLine}
              data={item}
              onPress={onPressDataHandler}
            />
          );
        }
        case DisplayType.badges: {
          return <BadgeSlide data={item} onPress={handleBadgeOnPress} />;
        }
        case DisplayType.tags: {
          return (
            <TagSlide
              data={item}
              onPress={onToggleItem}
              isSelected={() => isSelected(item)}
            />
          );
        }
        case DisplayType.featured: {
          return (
            <FeatureSlide data={{ advert: item }} onPress={onPressHandler} />
          );
        }
        case DisplayType.photo: {
          return (
            <PhotoSlide data={item} onPress={onPressDataHandler} margin={0} />
          );
        }
        case DisplayType.adverts: {
          return (
            <AdvertSlide
              data={{ advert: item }}
              onPress={onPressHandler}
              onPressCategory={onPressCategory}
              margin={0}
              onViewMore={onViewMore}
              showViewMore={showViewMore}
            />
          );
        }
        default: {
          return <Slide data={item} onPress={onPressDataHandler} />;
        }
      }
    },
    [
      displayType,
      onPress,
      onPressBadge,
      onPressCategory,
      onPressData,
      onViewMore,
      selectedItems,
      showViewMore,
      singleLine,
    ],
  );

  let calcItemWidth: number;
  let decelerationRate: number | 'normal' | 'fast' | undefined = 'fast';
  let data: Array<any> = [];
  switch (displayType) {
    case DisplayType.mini:
    case DisplayType.tags:
    case DisplayType.badges:
      calcItemWidth = totalMiniItemWidth;
      decelerationRate = 'normal';
      if (items) {
        data = [...items];
      } else {
        data = []; // categoryList;
      }
      break;
    case DisplayType.featured:
      calcItemWidth = totalFeatureItemWidth;
      if (items) {
        data = [...items];
      } else {
        data = [];
      }
      break;
    case DisplayType.photo:
      calcItemWidth = totalFeatureItemWidth;
      if (items) {
        data = [...items];
      } else {
        data = []; // categoryList;
      }
      break;
    case DisplayType.adverts:
      if (items) {
        data = [...items];
      } else {
        data = []; // categoryList;
      }
      calcItemWidth = totalItemWidth + itemCardPadding;
      break;
    default:
      calcItemWidth = totalItemWidth + itemCardPadding;
  }

  if (showViewMore && data.length > 0) {
    // generate a somewhat unqiue key for the item
    let imageUri = '';
    if (lodash.isArray(items) && items.length > 0) {
      const item = items[0];
      imageUri = lodash.get(item, 'hero_image.formats.medium.url', undefined);
      if (!imageUri) {
        imageUri = lodash.get(
          item,
          'hero_image.url',
          displayType?.toString() || displayType,
        );
      }
    }
    // debug(`${name} - adding viewmore item to data array`);
    data.push({ showViewMore, id: `${name}-lastItem-${imageUri}` });
  }

  const windowSize =
    displayType === DisplayType.mini ||
    displayType === DisplayType.tags ||
    displayType === DisplayType.badges
      ? 1000
      : Platform.OS === 'web'
      ? data.length * calcItemWidth || 5
      : 5;

  const flatListOptimizationProps = {
    ...Platform.select({
      web: {
        removeClippedSubviews: false,
        renderAll: true,
        // scrollEventThrottle: 16,
        windowSize,
        snapToInterval: calcItemWidth,
        keyExtractor: keyGen,
        getItemLayout: useCallback(
          (_, index) => ({
            length: calcItemWidth,
            offset: calcItemWidth * index,
            index,
          }),
          [calcItemWidth],
        ),
        showsHorizontalScrollIndicator: true,
      },
      default: {
        initialNumToRender: 0,
        maxToRenderPerBatch: 1,
        removeClippedSubviews: true,
        scrollEventThrottle: 16,
        windowSize,
        snapToInterval: calcItemWidth,
        keyExtractor: keyGen,
        getItemLayout: useCallback(
          (_, index) => ({
            length: calcItemWidth,
            offset: calcItemWidth * index,
            index,
          }),
          [calcItemWidth],
        ),
        showsHorizontalScrollIndicator: false,
        // props.displayType === DisplayType.mini ||
        // props.displayType === DisplayType.tags,
        bounces: true,
      },
    }),
  };

  // TODO: check performance / re-renders here
  // debug(`R:render carousel:${props.displayType}`);
  // debug(`R:${name} - render more: ${showViewMore}`);

  const loopDelaySecs = 5;

  if (loop) {
    return (
      <SwiperFlatList
        data={data}
        style={[styles.list]}
        renderItem={customRenderItem ? customRenderItem : renderItem}
        autoplay
        autoplayDelay={loopDelaySecs}
        autoplayLoop
        autoplayLoopKeepAnimation
        index={0}
        decelerationRate={decelerationRate}
        {...flatListOptimizationProps}
        viewabilityConfig={{
          minimumViewTime: config.advertTracking.carousel.minimumViewTime,
          itemVisiblePercentThreshold: undefined,
          viewAreaCoveragePercentThreshold:
            config.advertTracking.carousel.viewAreaCoveragePercentThreshold,
        }}
        onViewableItemsChanged={handleViewableItemsChanged}
      />
    );
  }

  return (
    <FlatList
      data={data}
      style={styles.list}
      renderItem={customRenderItem ? customRenderItem : renderItem}
      CellRendererComponent={renderCell}
      decelerationRate={decelerationRate}
      pagingEnabled
      horizontal
      {...flatListOptimizationProps}
      viewabilityConfig={{
        minimumViewTime: config.advertTracking.carousel.minimumViewTime,
        viewAreaCoveragePercentThreshold:
          config.advertTracking.carousel.viewAreaCoveragePercentThreshold,
      }}
      onViewableItemsChanged={handleViewableItemsChanged}
    />
  );
};

export interface State {
  seenLast: boolean;
}

class Carousel extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { seenLast: false }; // to manage analytics reporting
    debug(
      `props for ${props.name} are: ${JSON.stringify(props.items?.length)}`,
    );
  }

  handleViewableItemsChanged = ({
    changed,
    viewableItems,
  }: {
    changed: any[];
    viewableItems: any[];
  }) => {
    if (this.state.seenLast || !this.props.items?.length) {
      // debug('has seen last.  not updating');
      return;
    }
    if (viewableItems.length > 0) {
      const lastItem = viewableItems[viewableItems.length - 1];
      // debug(
      //   `tracking advert view ${[
      //     lastItem.index,
      //     this.props.items?.length,
      //     lastItem.index === this.props.items?.length - 1,
      //   ]}`,
      // );

      if (lastItem.index === this.props.items?.length - 1) {
        debug(`setting setHasSeenLast for carousel: ${this.props.name}`);
        this.setState({
          seenLast: true,
        });
      }
    }
    // debug(`tracking advert view. Has seen last:${this.state.seenLast}`);
    changed
      .filter((view) => view.isViewable && view.item && view.item.campaign)
      .forEach((view) => {
        trackAdvertView(view.item);
      });
  };

  render() {
    return (
      <CarouselFC
        {...this.props}
        handleViewableItemsChanged={this.handleViewableItemsChanged}
      />
    );
  }
}

export default Carousel;
